import type { ReactNode } from 'react';
import { Calendar, Info, ListChecks, Lock, Milestone as MilestoneIcon } from 'lucide-react';
import { useT } from '../../i18n';
import { taskDisplayCode } from '../../utils/identifiers';
import { dueLabel, isBlockedLabel, isOverdue } from './homeFormat';
import { useOpenTaskPanel } from './TaskPanel';

// A task on the home page drawn as the task card of the boards (same box,
// title, milestone line, due date, people and T-id, and the ⓘ that opens it in
// the side pane), so a card always reads as a task. Sized to its content;
// `footer` holds what the home page adds (status).

export function MiniTaskCard({ task, projectId, milestone, people, footer }: {
  task: {
    id: number;
    title: string;
    due_date: string | null;
    column_label: string;
    blocked_reason?: string | null;
    is_private?: boolean;
    can_open?: boolean;
    todo_done?: number;
    todo_total?: number;
  };
  /** The project it is shown under (the side pane's context). */
  projectId: number;
  /** "Jalon 2 · Définir le modèle" */
  milestone?: string | null;
  /** Who works on it (names), shown where the board shows the responsible. */
  people?: string | null;
  footer?: ReactNode;
}) {
  const { t } = useT();
  const overdue = isOverdue(task.due_date);
  const blocked = isBlockedLabel(task.column_label);
  const locked = task.can_open === false;
  const openPanel = useOpenTaskPanel();

  return (
    <div className={`task-card task-card--mini${blocked ? ' task-card--blocked' : ''}${locked ? ' task-card--locked' : ''}`}>
      {openPanel && !locked && (
        <button type="button" className="project-card-info" title={t('tasks.viewDetails')} aria-label={t('tasks.viewDetails')}
          onClick={() => openPanel(task.id, projectId)}>
          <Info size={14} />
        </button>
      )}
      {locked ? (
        <span className="task-card-title"><Lock size={11} aria-hidden="true" /> {t('home.who.privateTask')}</span>
      ) : (
        <a className="task-card-title" href={`#tasks?task=${task.id}`} title={task.title}>{task.title}</a>
      )}
      {blocked && task.blocked_reason && !locked && (
        <div className="task-card-blocked-reason task-card-blocked-reason--static">{task.blocked_reason}</div>
      )}
      {milestone && (
        <div className="task-card-milestone" title={milestone}>
          <MilestoneIcon size={11} aria-hidden="true" />
          <span>{milestone}</span>
        </div>
      )}
      <div className="task-card-meta">
        {task.due_date && (
          <span className={`task-card-due${overdue ? ' task-card-due--overdue' : ''}`} title={t('tasks.dueDate')}>
            <Calendar size={12} aria-hidden="true" />
            {dueLabel(task.due_date, t)}
          </span>
        )}
        {!!task.todo_total && (
          <span className="task-card-todos" title={t('home.tasks.todos', { done: task.todo_done ?? 0, total: task.todo_total })}>
            <ListChecks size={12} aria-hidden="true" />{task.todo_done ?? 0}/{task.todo_total}
          </span>
        )}
        {people && <span className="task-card-assignee" title={people}>{people}</span>}
        {task.is_private && !locked && (
          <span className="task-card-private-lock" title={t('tasks.privateTaskHelp')} aria-label={t('tasks.privateTask')}><Lock size={12} /></span>
        )}
        <span className="card-id">{taskDisplayCode(task)}</span>
      </div>
      {footer && <div className="task-card-foot">{footer}</div>}
    </div>
  );
}
